import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError, FulfillmentOrder, FulfillmentPerformance } from '../api';
import { boxOfficeEndpoints, eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * Mail and will call (D-58): the orders for a performance whose tickets
 * are to be posted or held at the window, and the batch that prints them on
 * a BOCA — a cover ticket per order (customer, address, what is inside)
 * followed by the tickets. Printing an order a second time reissues its
 * tickets first, so the earlier paper stops admitting.
 *
 * **The batch can be a night rather than a show (item 75, D-310).** A matinee
 * and an evening are one queue of people and one stack of paper, so "the whole
 * night" widens the scope to every show at the same venue within eight hours
 * either side — far enough for an afternoon and a late show, not far enough to
 * reach tomorrow. Which shows that turned out to be is printed on the screen,
 * because a scope somebody cannot see is a scope somebody will get wrong.
 */

/** Eight hours either side: a 2pm matinee and a 10pm late show, and nothing tomorrow. */
const NIGHT_HOURS = 8;
export default function FulfillmentPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [eventId, setEventId] = useState('');
  const [performanceId, setPerformanceId] = useState('');
  const [method, setMethod] = useState<'MAIL' | 'WILL_CALL'>('MAIL');
  const [printerId, setPrinterId] = useState('');
  const [includePrinted, setIncludePrinted] = useState(false);
  const [wholeNight, setWholeNight] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const events = useQuery({ queryKey: ['events-list', 'PUBLISHED'], queryFn: () => eventEndpoints.list('PUBLISHED') });
  const event = events.data?.events.find((e) => e.id === eventId) ?? null;
  const performances = useQuery({ queryKey: ['event', eventId, 'performances'], queryFn: () => eventEndpoints.performances(eventId), enabled: Boolean(eventId) });
  const venue = useQuery({ queryKey: ['box-office', 'venue', event?.primary_venue_id], queryFn: () => boxOfficeEndpoints.venue(event!.primary_venue_id), enabled: Boolean(event) });
  const printers = useMemo(() => (venue.data?.printers ?? []).filter((p) => p.printer_model === 'BOCA_FGL' && p.active), [venue.data]);
  const chosen = (performances.data?.performances ?? []).find((p) => p.id === performanceId) ?? null;
  const nightWindow = useMemo(() => {
    if (!wholeNight || !chosen) return null;
    const at = Date.parse(chosen.starts_at);
    const iso = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, '.000Z');
    return { from: iso(at - NIGHT_HOURS * 3_600_000), to: iso(at + NIGHT_HOURS * 3_600_000) };
  }, [wholeNight, chosen]);

  const orders = useQuery<{ performances: FulfillmentPerformance[]; orders: FulfillmentOrder[] }, ApiError>({
    queryKey: ['fulfillment', performanceId, method, includePrinted, nightWindow?.from ?? '', nightWindow?.to ?? ''],
    queryFn: () =>
      nightWindow && event
        ? boxOfficeEndpoints.fulfillment({ ...nightWindow, venue_id: event.primary_venue_id, method, include_printed: includePrinted })
        : boxOfficeEndpoints.fulfillment({ performance_id: performanceId, method, include_printed: includePrinted }),
    enabled: Boolean(performanceId),
  });
  const scope = orders.data?.performances ?? [];
  const print = useMutation({
    mutationFn: (orderIds: string[] | null) =>
      boxOfficeEndpoints.printFulfillment({ performance_ids: scope.length > 0 ? scope.map((p) => p.id) : [performanceId], method, printer_id: printerId, ...(orderIds ? { order_ids: orderIds } : {}), include_printed: false }),
    onSuccess: (result) => {
      setError(null);
      setNotice(`Queued ${result.orders} order${result.orders === 1 ? '' : 's'}: ${result.header_job_ids.length} cover ticket${result.header_job_ids.length === 1 ? '' : 's'} and ${result.ticket_job_ids.length} ticket${result.ticket_job_ids.length === 1 ? '' : 's'} to the printer.`);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['fulfillment'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!can('orders.view')) return <NotPermitted permission="orders.view" />;
  const rows = orders.data?.orders ?? [];
  const unprinted = rows.filter((o) => !o.fulfillment_printed_at);
  const canPrint = can('box_office.reprint') && Boolean(printerId) && Boolean(performanceId);

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded-lg border border-ok/30 bg-ok-soft px-4 py-2 text-sm text-ink-900">{notice}</p> : null}
      <Panel title="Mail & will call" description={`Pick ${dateWords().a} and a delivery method. Print all queues a cover ticket per order (customer, address, what is inside) followed by its tickets on the chosen BOCA; an order printed before is reissued first, so the earlier paper stops admitting.`}>
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Event</span>
            <select className={`mt-1 ${INPUT}`} value={eventId} onChange={(e) => { setEventId(e.target.value); setPerformanceId(''); setPrinterId(''); setSelected(new Set()); }}>
              <option value="">Choose an event</option>
              {(events.data?.events ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">{dateWords().One}</span>
            <select className={`mt-1 ${INPUT}`} value={performanceId} disabled={!eventId} onChange={(e) => { setPerformanceId(e.target.value); setSelected(new Set()); }}>
              <option value="">Choose {dateWords().a}</option>
              {(performances.data?.performances ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {inZone(p.starts_at, p.timezone)} {p.name_override ? `· ${p.name_override}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Delivery</span>
            <select className={`mt-1 ${INPUT}`} value={method} onChange={(e) => { setMethod(e.target.value as 'MAIL' | 'WILL_CALL'); setSelected(new Set()); }}>
              <option value="MAIL">Mail</option>
              <option value="WILL_CALL">Will call</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-semibold tracking-wide text-ink-400 uppercase">Printer</span>
            <select className={`mt-1 ${INPUT}`} value={printerId} disabled={!event} onChange={(e) => setPrinterId(e.target.value)}>
              <option value="">{event ? (printers.length === 0 ? 'No BOCA printers at this venue' : 'Choose a printer') : 'Pick an event first'}</option>
              {printers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.last_status && p.last_status !== 'ONLINE' ? `(${p.last_status.toLowerCase()})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-ink-700">
            <input type="checkbox" checked={includePrinted} onChange={(e) => setIncludePrinted(e.target.checked)} /> Show already printed
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-ink-700">
            <input type="checkbox" checked={wholeNight} disabled={!performanceId} onChange={(e) => { setWholeNight(e.target.checked); setSelected(new Set()); }} /> The whole night
          </label>
          <div className="ml-auto flex gap-2 pb-0.5">
            <Button variant="primary" disabled={!canPrint || print.isPending || unprinted.length === 0} onClick={() => print.mutate(null)}>
              {print.isPending ? 'Queuing…' : `Print all unprinted (${unprinted.length})`}
            </Button>
            <Button disabled={!canPrint || print.isPending || selected.size === 0} onClick={() => print.mutate([...selected])}>
              Print selected ({selected.size})
            </Button>
          </div>
        </div>
        {orders.error ? <ErrorNote>{orders.error.message}</ErrorNote> : null}
        {/* What the scope turned out to mean. Unstated, "the whole night" is a guess. */}
        {wholeNight && scope.length > 0 ? (
          <p className="border-t border-line px-5 py-2 text-xs text-ink-500">
            Printing for {scope.length} show{scope.length === 1 ? '' : 's'}: {scope.map((p) => `${p.event_title} ${inZone(p.starts_at, p.timezone)}`).join(' · ')}
          </p>
        ) : null}
        <Table headers={[' ', 'Order', 'Customer', method === 'MAIL' ? 'Mail to' : 'Pickup', 'Tickets', 'Printed']}>
          {!performanceId ? (
            <EmptyRow columns={6}>Choose an event and {dateWords().a}.</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={6}>{includePrinted ? `No ${method === 'MAIL' ? 'mail' : 'will-call'} orders for this ${dateWords().one}.` : `Nothing waiting to print. Tick "Show already printed" to reprint.`}</EmptyRow>
          ) : (
            rows.map((o) => (
              <tr key={o.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <input type="checkbox" checked={selected.has(o.id)} onChange={(e) => setSelected((s) => { const next = new Set(s); if (e.target.checked) next.add(o.id); else next.delete(o.id); return next; })} aria-label={`Select ${o.order_number}`} />
                </td>
                <td className="px-5 py-3">
                  <Link to={`/orders/${o.id}`} className="font-mono font-medium text-accent hover:underline">
                    {o.order_number}
                  </Link>
                  <div className="text-xs text-ink-400">{o.completed_at ? formatWhen(o.completed_at) : ''}</div>
                </td>
                <td className="px-5 py-3">
                  <div className="text-ink-900">{o.customer_name ?? 'Guest'}</div>
                  <div className="text-xs text-ink-400">{[o.customer_email, o.customer_phone].filter(Boolean).join(' · ')}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {method === 'MAIL' ? (
                    o.address ? (
                      <div className="whitespace-pre-line">{[o.address.recipient_name, o.address.address_line1, o.address.address_line2, `${o.address.city}, ${o.address.region} ${o.address.postal_code}`].filter(Boolean).join('\n')}</div>
                    ) : (
                      <span className="text-bad">No address on file</span>
                    )
                  ) : o.pickups > 0 ? (
                    <Badge tone="ok">picked up</Badge>
                  ) : (
                    <Badge tone="neutral">waiting</Badge>
                  )}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {o.tickets.length} · {o.tickets.map((t) => t.seat_label).join(', ')}
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{o.fulfillment_printed_at ? formatWhen(o.fulfillment_printed_at) : <span className="text-ink-400">not yet</span>}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
